import { UnifiedDriver } from '@/types/driver'
import { Autocomplete, Button, ComboboxItem, Group, Modal, OptionsFilter, Switch, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useMemo, useState } from 'react'
import { TbMail, TbPhone, TbUser } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'

export enum DriverModalType {
  IMPORT = 'import',
  LINK = 'link',
  EDIT = 'edit',
  CREATE = 'create',
}

const ImportDialog = ({ driver }: { driver?: UnifiedDriver | null }) => {
  const { t } = useTranslation('drivers')
  return <Text mb="md">{t('confirmImportDriver', { name: driver?.name })}</Text>
}

const LinkDialog = ({
  driver,
  internalDrivers,
  onConfirm,
  onCancel,
}: {
  driver?: UnifiedDriver | null
  internalDrivers: UnifiedDriver[]
  onConfirm: (selectedDriver: UnifiedDriver) => void
  onCancel: () => void
}) => {
  const { t } = useTranslation(['drivers', 'common'])
  const [selectedValue, setSelectedValue] = useState<string>('')
  const [selectedDriver, setSelectedDriver] = useState<UnifiedDriver | null>(null)

  const optionsFilter: OptionsFilter = ({ options, search }) => {
    const normalizeString = (str: string) =>
      str
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

    const filtered = (options as ComboboxItem[]).filter(option => {
      const normalizedLabel = normalizeString(option.label)
      const normalizedSearch = normalizeString(search || '')
      return normalizedLabel.includes(normalizedSearch)
    })

    filtered.sort((a, b) => a.label.localeCompare(b.label))
    return filtered
  }

  const data = useMemo(() => {
    return [...new Set([...internalDrivers.map(driver => driver.name), driver?.name].filter(Boolean) as string[])]
  }, [internalDrivers, driver]).map(name => ({
    label: name,
    value: name,
  }))

  return (
    <>
      <Text mb="md">{t('confirmLinkDriver', { name: driver?.name })}</Text>
      <Autocomplete
        data={data}
        placeholder={t('searchInternalDriver')}
        mb="md"
        value={selectedValue}
        onChange={value => {
          setSelectedValue(value)
          const foundDriver = internalDrivers.find(driver => driver.name === value)
          setSelectedDriver(foundDriver || null)
        }}
        filter={optionsFilter}
      />
      <Group justify="space-between" mt="md">
        <Button onClick={onCancel} color="gray">
          {t('cancel', { ns: 'common' })}
        </Button>
        <Button
          onClick={() => (selectedDriver ? onConfirm(selectedDriver) : undefined)}
          disabled={!selectedDriver}
          color="blue"
        >
          {t('linkToSelectedDriver', { name: selectedDriver?.name || t('selectedDriver') })}
        </Button>
      </Group>
    </>
  )
}

const EditDialog = ({
  driver,
  onConfirm,
  onCancel,
}: {
  driver?: UnifiedDriver | null
  onConfirm: (updatedDriver: UnifiedDriver) => void
  onCancel: () => void
}) => {
  const { t } = useTranslation(['drivers', 'common'])
  const [active, setActive] = useState(driver?.active || false)

  const values = useForm({
    initialValues: {
      name: driver?.name || '',
      phone: driver?.phone || '',
      email: driver?.email || '',
    },
    validate: {
      name: (value: string) => (value && value.trim() !== '' ? null : t('nameRequired')),
      email: (value: string) =>
        value && value.trim() !== '' && !/^\S+@\S+$/.test(value) ? t('invalidEmailFormat') : null,
    },
  })

  const handleSubmit = (formValues: typeof values.values) => {
    if (driver) {
      const updatedDriver: UnifiedDriver = {
        ...driver,
        name: formValues.name,
        phone: formValues.phone,
        email: formValues.email,
        active,
      }
      onConfirm(updatedDriver)
    }
  }

  return (
    <form onSubmit={values.onSubmit(handleSubmit)}>
      <TextInput
        mb="md"
        placeholder={t('name')}
        label={t('name')}
        {...values.getInputProps('name')}
        leftSection={<TbUser />}
      />
      <TextInput
        mb="md"
        placeholder={t('phone')}
        label={t('phone')}
        {...values.getInputProps('phone')}
        leftSection={<TbPhone />}
      />
      <TextInput
        mb="md"
        placeholder={t('email')}
        label={t('email')}
        {...values.getInputProps('email')}
        leftSection={<TbMail />}
      />
      <Switch mb="md" label={t('active')} checked={active} onChange={() => setActive(!active)} />
      <Group justify="space-between" mt="md">
        <Button onClick={onCancel} color="gray">
          {t('cancel', { ns: 'common' })}
        </Button>
        <Button type="submit" color="blue">
          {t('saveChanges')}
        </Button>
      </Group>
    </form>
  )
}

const CreateDialog = ({
  onConfirm,
  onCancel,
  showWarning,
}: {
  onConfirm: (createdDriver: UnifiedDriver) => void
  onCancel: () => void
  showWarning: boolean
}) => {
  const { t } = useTranslation(['drivers', 'common'])
  const [active, setActive] = useState(true)

  const values = useForm({
    initialValues: {
      name: '',
      phone: '',
      email: '',
    },
    validate: {
      name: (value: string) => (value && value.trim() !== '' ? null : t('nameRequired')),
      email: (value: string) =>
        value && value.trim() !== '' && !/^\S+@\S+$/.test(value) ? t('invalidEmailFormat') : null,
    },
  })

  const handleSubmit = (formValues: typeof values.values) => {
    if (formValues.name) {
      onConfirm({
        ...formValues,
        id: '',
        canLink: false,
        canImport: false,
        type: 'internal',
        active,
      })
    }
  }

  return (
    <>
      <form key="create-driver-form" onSubmit={values.onSubmit(handleSubmit)}>
        <TextInput
          mb="md"
          placeholder={t('name')}
          label={t('name')}
          {...values.getInputProps('name')}
          leftSection={<TbUser />}
        />
        <TextInput
          mb="md"
          placeholder={t('phone')}
          label={t('phone')}
          {...values.getInputProps('phone')}
          leftSection={<TbPhone />}
        />
        <TextInput
          mb="md"
          placeholder={t('email')}
          label={t('email')}
          {...values.getInputProps('email')}
          leftSection={<TbMail />}
        />
        <Switch mb="md" label={t('active')} checked={active} onChange={() => setActive(!active)} />
        {showWarning && (
          <Text
            mb="md"
            bg="light-dark(var(--mantine-color-yellow-1), var(--mantine-color-yellow-3))"
            c="var(--mantine-color-dark-9)"
            p="md"
            styles={{ root: { borderRadius: '4px' } }}
          >
            <strong>{t('warning', { ns: 'common' })}:</strong> {t('planujSmenyWarning')}
          </Text>
        )}
        <Group justify="space-between" mt="md">
          <Button onClick={onCancel} color="gray">
            {t('cancel', { ns: 'common' })}
          </Button>
          <Button type="submit" color="blue">
            {t('createDriver')}
          </Button>
        </Group>
      </form>
    </>
  )
}

function DriverModal({
  driver,
  type,
  opened,
  onClose,
  onConfirm,
  internalDrivers,
  showCreateWarning,
}: {
  driver?: UnifiedDriver | null
  type: DriverModalType | null
  opened: boolean
  onClose: () => void
  onConfirm: (updatedDriver?: UnifiedDriver, selectedDriver?: UnifiedDriver) => void
  internalDrivers: UnifiedDriver[]
  showCreateWarning: boolean
}) {
  const { t } = useTranslation(['drivers', 'common'])
  const body = useMemo(() => {
    if (type === DriverModalType.IMPORT) return <ImportDialog driver={driver} />
    if (type === DriverModalType.LINK)
      return (
        <LinkDialog
          onCancel={onClose}
          driver={driver}
          internalDrivers={internalDrivers}
          onConfirm={selectedDriver => onConfirm(selectedDriver)}
        />
      )
    if (type === DriverModalType.EDIT)
      return <EditDialog driver={driver} onConfirm={updatedDriver => onConfirm(updatedDriver)} onCancel={onClose} />
    if (type === DriverModalType.CREATE)
      return (
        <CreateDialog
          onConfirm={createdDriver => onConfirm(createdDriver)}
          onCancel={onClose}
          showWarning={showCreateWarning}
        />
      )
  }, [type, driver, internalDrivers, onConfirm, showCreateWarning, onClose])

  const title = useMemo(() => {
    if (type === DriverModalType.IMPORT) return t('importDriver')
    if (type === DriverModalType.LINK) return t('linkDriver')
    if (type === DriverModalType.EDIT) return t('editDriver')
    if (type === DriverModalType.CREATE) return t('createInternalDriver')
  }, [type, t])

  const handleConfirm = () => {
    if (type === DriverModalType.IMPORT) {
      onConfirm()
    }
  }

  const handleCancel = () => {
    onClose()
  }

  return (
    type && (
      <Modal opened={opened} onClose={handleCancel} title={title} size="lg" centered>
        {body}
        {type === DriverModalType.IMPORT && (
          <Group justify="space-between" mt="md">
            <Button onClick={handleCancel} color="gray">
              {t('cancel', { ns: 'common' })}
            </Button>
            <Button onClick={handleConfirm} color="blue">
              {t('import')}
            </Button>
          </Group>
        )}
      </Modal>
    )
  )
}

export default DriverModal
